/**
 * useAccess Hook
 *
 * This hook provides a standardized way to evaluate user permissions and access control flags
 * throughout the application. It returns an object with boolean flags for each permission type
 * (read, edit, create, delete, archive) as well as composite flags like readonly, disabled, and canManage.
 *
 * Usage:
 * // { canRead, canEdit, canCreate, canDelete, canArchive, readonly, disabled, canManage } = useAccess(userPermissions)

 *   const access = useAccess(userPermissions)
 *   if (access.canEdit) { ... }
 *   if (access.canManage) { ... }
 *   if (canRead) { ... }
 *
 * - Use this hook to simplify permission checks in your components.
 * - Update the RULES object to add or modify composite access logic as needed.
 *
 * See usage examples at the bottom of this file.
 */

import { useMemo } from 'react'

export type Permission = 'ARCHIVE' | 'CREATE' | 'READ' | 'DELETE' | 'EDIT'

export interface PermissionList extends Array<Permission | string> {}

export interface RuleNameMap {
  readonlyIfReadNoEdit: string
  disabledIfNoRead: string
  canManageIfDeleteOrArchive: string
}

type RuleName = keyof RuleNameMap

const RULES: Record<RuleName, (p: Set<string>) => boolean> = {
  readonlyIfReadNoEdit: (p) => p.has('READ') && !p.has('EDIT'),
  disabledIfNoRead: (p) => !p.has('READ'),
  canManageIfDeleteOrArchive: (p) => p.has('DELETE') || p.has('ARCHIVE')
}

interface AccessResult {
  canRead: boolean
  canEdit: boolean
  canCreate: boolean
  canDelete: boolean
  canArchive: boolean
  readonly: boolean
  disabled: boolean
  canManage: boolean
}

const evaluateAccess = (permissions: PermissionList): AccessResult => {
  const set = new Set(permissions ?? [])

  return {
    canRead: set.has('READ'),
    canEdit: set.has('EDIT'),
    canCreate: set.has('CREATE'),
    canDelete: set.has('DELETE'),
    canArchive: set.has('ARCHIVE'),

    readonly: RULES.readonlyIfReadNoEdit(set),
    disabled: RULES.disabledIfNoRead(set),

    // future example
    canManage: RULES.canManageIfDeleteOrArchive(set)
  }
}

export const useAccess = (
  permissions: PermissionList
): ReturnType<typeof evaluateAccess> => {
  return useMemo(() => evaluateAccess(permissions), [permissions])
}
